import type { CSSProperties } from "react";

export type ThemeColorKey =
  | "brand"
  | "brandBold"
  | "brandSubtle"
  | "canvas"
  | "elevation1"
  | "elevation2"
  | "elevation3"
  | "elevation4"
  | "gray850"
  | "border"
  | "fg"
  | "fgMuted"
  | "fgSubtle"
  | "fgOnPrimary"
  | "success"
  | "warning"
  | "error"
  | "highlight";

export type ThemeColor = {
  key: ThemeColorKey;
  cssVar: string;
  label: string;
  fallback: string;
};

export const THEME_COLORS: ThemeColor[] = [
  { key: "brand", cssVar: "--color-brand", label: "Brand", fallback: "#623cff" },
  { key: "brandBold", cssVar: "--color-brand-bold", label: "Brand, bold", fallback: "#9e82ff" },
  { key: "brandSubtle", cssVar: "--color-brand-subtle", label: "Brand, subtle", fallback: "#4425c4" },
  { key: "canvas", cssVar: "--color-canvas", label: "Canvas", fallback: "#000000" },
  { key: "gray850", cssVar: "--color-gray-850", label: "Elevation 0", fallback: "#1a1a1d" },
  { key: "elevation1", cssVar: "--color-elevation-1", label: "Elevation 1", fallback: "#161618" },
  { key: "elevation2", cssVar: "--color-elevation-2", label: "Elevation 2", fallback: "#202024" },
  { key: "elevation3", cssVar: "--color-elevation-3", label: "Elevation 3", fallback: "#2a2a2f" },
  { key: "elevation4", cssVar: "--color-elevation-4", label: "Elevation 4", fallback: "#3e3e45" },
  { key: "border", cssVar: "--color-border", label: "Border", fallback: "#2a2a2f" },
  { key: "fg", cssVar: "--color-fg", label: "Text", fallback: "#ffffff" },
  { key: "fgMuted", cssVar: "--color-fg-muted", label: "Text, muted", fallback: "#d8d8dd" },
  { key: "fgSubtle", cssVar: "--color-fg-subtle", label: "Text, subtle", fallback: "#9898a2" },
  { key: "fgOnPrimary", cssVar: "--color-fg-on-primary", label: "Text on brand", fallback: "#ffffff" },
  { key: "success", cssVar: "--color-success", label: "Success", fallback: "#1eae5a" },
  { key: "warning", cssVar: "--color-warning", label: "Warning", fallback: "#e8a33d" },
  { key: "error", cssVar: "--color-error", label: "Error", fallback: "#df1642" },
  { key: "highlight", cssVar: "--color-highlight", label: "Highlight", fallback: "#6fff67" },
];

export const RADIUS_STYLES = ["Default", "Sharp", "Rounded"];

/* How brand-coloured surfaces fill: flat, or a gradient through the brand
   colour, lighter at the top left (Hugo Casino's orange buttons). */
export const BUTTON_FILLS = ["Solid", "Gradient"];

/* Page headings as written, or in capitals (Rigel's wide display type). */
export const HEADING_CASES = ["Normal", "Uppercase"];

/* Buttons, icon buttons and menu links with the theme's corners, or fully
   round pills (Altair's). */
export const BUTTON_SHAPES = ["Default", "Pill"];

/* Ambient motion: none, or Night sky — twinkling stars behind the site, a
   light sweeping the hero headline after a blinking caret, a glow running
   round the brand buttons, pulsing dots before section titles (Arcturus's,
   after bitcoincasino.com). Reduced-motion visitors get the stills. */
export const MOTION_STYLES = ["None", "Night sky"];

/* Buttons, the top bar's links and the menu links in the body face as
   written, or Display: in the heading face, in capitals and spaced out
   (Capella's, after Betmode's condensed display type). */
export const BUTTON_TYPES = ["Body", "Display"];

/* The page's surfaces (cards, rails, the bar's fields) in their colours, or
   Glass: the same colours let partly through, so the site backdrop shows
   behind them (Bahia.bet's gold). Dialogs and drop-downs stay solid. */
export const SURFACE_STYLES = ["Solid", "Glass"];
const GLASS_KEYS: ThemeColorKey[] = ["gray850", "elevation1", "elevation2", "elevation3", "elevation4"];

export const FONT_FAMILIES = [
  "Inter",
  "Manrope",
  "Sora",
  "Space Grotesk",
  "Outfit",
  "DM Sans",
  "Bricolage Grotesque",
  "JetBrains Mono",
  "Exo 2",
  "Montserrat",
  "Poppins",
  "Rubik",
  "Roboto",
  "Unbounded",
  "Oxanium",
  "Big Shoulders Display",
  "Mulish",
  "Cinzel",
] as const;

export type FontFamily = (typeof FONT_FAMILIES)[number];

export const DEFAULT_FONT: FontFamily = FONT_FAMILIES[0];

export const FONT_FALLBACKS: Record<string, string> = {
  Sans: "ui-sans-serif, system-ui, sans-serif",
  Mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
  Serif: "ui-serif, Georgia, serif",
};

export const FONT_FALLBACK_NAMES = Object.keys(FONT_FALLBACKS);

export type CustomFont = { name: string; src: string; fallback: string };

export const MAX_CUSTOM_FONTS = 4;

const FONT_NAME = /^[a-z0-9][a-z0-9 _-]{0,39}$/i;

const FONT_SRC =
  /^(?:data:font\/(?:woff2|woff|ttf|otf);base64,[a-z0-9+/=]+|https:\/\/[a-z0-9._~:/?#@!$&*+,;=%-]+)$/i;

export const isFontName = (value: string) => FONT_NAME.test(value.trim());

export const isFontSource = (value: string) => FONT_SRC.test(value.trim());

const FORMAT: Record<string, string> = {
  woff2: "woff2",
  woff: "woff",
  ttf: "truetype",
  otf: "opentype",
};

export function canonicalFontSource(dataUrl: string, filename: string) {
  const encoded = /^data:[^;,]*;base64,([a-z0-9+/=]+)$/i.exec(dataUrl.trim());
  const extension = (/\.([a-z0-9]+)$/i.exec(filename.trim())?.[1] ?? "").toLowerCase();

  if (!encoded || !(extension in FORMAT)) return "";

  return `data:font/${extension};base64,${encoded[1]}`;
}

const sourceFormat = (src: string) => {
  const data = /^data:font\/([a-z0-9]+);/i.exec(src);
  const extension = data ? data[1] : /\.([a-z0-9]+)(?:[?#]|$)/i.exec(src)?.[1];
  return FORMAT[(extension ?? "").toLowerCase()] ?? "";
};

export function usableFonts(theme: SiteTheme): CustomFont[] {
  const fonts = Array.isArray(theme.customFonts) ? theme.customFonts : [];
  const seen = new Set<string>();

  return fonts.filter((font) => {
    const name = (font?.name ?? "").trim();
    const key = name.toLowerCase();
    if (!isFontName(name) || !isFontSource(font?.src ?? "") || seen.has(key)) return false;
    if (FONT_FAMILIES.some((family) => family.toLowerCase() === key)) return false;
    seen.add(key);
    return true;
  });
}

export function fontFace(font: CustomFont) {
  const format = sourceFormat(font.src);
  const source = format ? `url(${font.src.trim()}) format("${format}")` : `url(${font.src.trim()})`;

  return `@font-face{font-family:"${font.name.trim()}";src:${source};font-weight:100 900;font-style:normal;font-display:swap;}`;
}

export const fontStack = (name: string, fallback: string) =>
  `"${name.trim()}", ${FONT_FALLBACKS[fallback] ?? FONT_FALLBACKS.Sans}`;

export type SiteTheme = Record<ThemeColorKey, string> & {
  fontBody: string;
  fontHeading: string;
  radius: string;
  /* BUTTON_FILLS. */
  buttonFill: string;
  /* HEADING_CASES: the page's h1-h3 as written, or in capitals. */
  headingCase: string;
  /* BUTTON_SHAPES: buttons and menu links with the theme's corners, or pills. */
  buttonShape: string;
  /* An image fixed behind the whole site, under every page (Rigel's
     atmosphere). Blank is the plain canvas colour. */
  backdropUrl: string;
  /* MOTION_STYLES. */
  motion: string;
  /* BUTTON_TYPES. */
  buttonType: string;
  /* SURFACE_STYLES. */
  surfaces?: string;
  customFonts: CustomFont[];
};

const VAR = Object.fromEntries(THEME_COLORS.map((color) => [color.key, color.cssVar])) as Record<
  ThemeColorKey,
  string
>;

export const DEFAULT_PALETTE = Object.fromEntries(
  THEME_COLORS.map((color) => [color.key, color.fallback]),
) as Record<ThemeColorKey, string>;

const FALLBACK = DEFAULT_PALETTE;

const ALIASES: [string, ThemeColorKey][] = [
  ["--color-border-input", "elevation2"],
  ["--color-border-input-disabled", "gray850"],
  ["--color-fg-disabled", "elevation4"],
  ["--color-fg-on-disabled", "fgSubtle"],
];

const tint = (key: ThemeColorKey, percent: number) =>
  `color-mix(in srgb, var(${VAR[key]}) ${percent}%, #ffffff)`;

const shade = (key: ThemeColorKey, percent: number) =>
  `color-mix(in srgb, var(${VAR[key]}) ${percent}%, #000000)`;

const DERIVED: [string, ThemeColorKey, string][] = [
  ["--color-brand-bold", "brand", tint("brand", 63)],
  ["--color-brand-subtle", "brand", shade("brand", 69)],
  ["--color-brand-fg", "brand", tint("brand", 6)],
  ["--color-brand-fg-accent", "brand", tint("brand", 28)],
  ["--color-brand-fg-tertiary", "brand", `color-mix(in srgb, var(${VAR.brand}) 45%, var(${VAR.fgMuted}))`],
  ["--color-brand-bg-deepest", "brand", shade("brand", 12)],
  ["--color-brand-bg-deep", "brand", shade("brand", 16)],
  ["--color-brand-bg-medium", "brand", shade("brand", 21)],
  ["--color-brand-bg-soft", "brand", shade("brand", 29)],
  ["--color-brand-secondary", "brand", shade("brand", 55)],
  ["--color-brand-secondary-fg", "brand", tint("brand", 10)],
  ["--color-success-fg", "success", tint("success", 72)],
  ["--color-warning-fg", "warning", tint("warning", 70)],
  ["--color-error-fg", "error", tint("error", 75)],
];

const RADIUS_SCALE: Record<string, Record<string, string>> = {
  Sharp: {
    "--radius-sm": "0px",
    "--radius-md": "2px",
    "--radius-lg": "3px",
    "--radius-xl": "4px",
    "--radius-2xl": "6px",
  },
  Rounded: {
    "--radius-sm": "5px",
    "--radius-md": "10px",
    "--radius-lg": "14px",
    "--radius-xl": "18px",
    "--radius-2xl": "22px",
  },
};

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/;

export const isThemeColor = (value: string) => HEX.test(value.trim().toLowerCase());

export function themeColor(theme: SiteTheme, key: ThemeColorKey) {
  const value = (theme[key] ?? "").trim().toLowerCase();
  return isThemeColor(value) ? value : FALLBACK[key];
}

function overridden(theme: SiteTheme, key: ThemeColorKey) {
  const value = themeColor(theme, key);
  return value === FALLBACK[key] ? "" : value;
}

function channels(hex: string) {
  const value = hex.replace("#", "");
  const full = value.length === 3 ? [...value].map((c) => c + c).join("") : value;
  return [0, 2, 4].map((at) => Number.parseInt(full.slice(at, at + 2), 16));
}

export function readableOn(background: string) {
  const [r, g, b] = channels(background).map((channel) => {
    const part = channel / 255;
    return part <= 0.04045 ? part / 12.92 : ((part + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const onWhite = 1.05 / (luminance + 0.05);
  const onBlack = (luminance + 0.05) / 0.05;

  return onBlack > onWhite ? "#000000" : "#ffffff";
}

export function themeStyle(theme: SiteTheme): CSSProperties | undefined {
  const style: Record<string, string> = {};

  for (const { key, cssVar } of THEME_COLORS) {
    const value = overridden(theme, key);
    if (value) style[cssVar] = value;
  }

  /* Text on a new brand is picked for contrast unless the theme names it —
     a named white included, though it matches the shipped value: Vega's
     orange reads black by the numbers, and its buttons are white-lettered. */
  const brand = overridden(theme, "brand");
  const namedOnPrimary = isThemeColor((theme.fgOnPrimary ?? "").trim().toLowerCase());
  if (brand && !namedOnPrimary) {
    style["--color-fg-on-primary"] = readableOn(brand);
  }

  for (const [cssVar, source] of ALIASES) {
    if (overridden(theme, source)) style[cssVar] = `var(${VAR[source]})`;
  }

  for (const [cssVar, source, expression] of DERIVED) {
    if (cssVar in style) continue;
    if (overridden(theme, source)) style[cssVar] = expression;
  }

  Object.assign(style, RADIUS_SCALE[theme.radius] ?? {});

  /* Glass: each surface at 78% over the backdrop (enough for text to keep
     5:1 over a dark picture, Bahia.bet's samba); its solid colour stays on
     --solid-* for the dialogs and drop-downs globals.css keeps opaque. */
  if (theme.surfaces === "Glass") {
    for (const key of GLASS_KEYS) {
      const solid = themeColor(theme, key);
      style[`--solid-${VAR[key].replace("--color-", "")}`] = solid;
      style[VAR[key]] = `color-mix(in srgb, ${solid} 78%, transparent)`;
    }
  }

  return Object.keys(style).length > 0 ? (style as CSSProperties) : undefined;
}

/* Forces every token back to what the build ships, regardless of what an
   ancestor has set — the real root layout carries the operator's theme onto
   every route including /admin. The admin chrome itself should stay stable
   no matter what an operator has configured; only the live preview pane
   inside it shows their theme. Values mirror globals.css's @theme block. */
export function defaultThemeStyle(): CSSProperties {
  return {
    ...Object.fromEntries(THEME_COLORS.map((color) => [color.cssVar, color.fallback])),
    "--radius-sm": "3px",
    "--radius-md": "6px",
    "--radius-lg": "9px",
    "--radius-xl": "12px",
    "--radius-2xl": "15px",
    /* A real h1–h6 has its own `font-family: var(--font-heading)` rule, which
       beats plain inheritance — so forcing font-family on this element alone
       (e.g. via next/font's `.className`) never reaches those tags. Only
       resolves correctly if `inter.variable` is also present in scope to
       define --font-inter. */
    "--font-body": "var(--font-inter), ui-sans-serif, system-ui, sans-serif",
    "--font-heading": "var(--font-body)",
  } as CSSProperties;
}

export const resolvedPalette = (theme: SiteTheme) =>
  Object.fromEntries(THEME_COLORS.map(({ key }) => [key, themeColor(theme, key)])) as Record<
    ThemeColorKey,
    string
  >;
